<template>
  <div>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <OrganizationApiUsageDialog
      v-if="showApiUsageDialog"
      v-model="showApiUsageDialog"
      :organization="selectedOrganization"
    />

    <v-card v-else flat>
      <v-data-table
        :headers="visibleHeaders"
        :items="filteredOrganizations"
        :items-per-page="10"
        class="elevation-0 custom-data-table"
        :no-data-text="noDataText"
        :height="tableHeight"
        fixed-header
        :loading="tableLoading"
        density="compact"
      >
        <template v-slot:top>
          <div class="d-flex justify-space-between align-center pa-4">
            <span class="text-h5">Organizations</span>
            <div class="d-flex align-center">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                variant="outlined"
                density="compact"
                style="width: 350px; margin-right: 16px"
              ></v-text-field>
              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                @click="openCreateDialog"
              >
                Create Organization
              </v-btn>
              <v-btn
                color="primary"
                class="ml-2"
                prepend-icon="mdi-cog"
                @click="openSettingsDialog"
                >settings</v-btn
              >
            </div>
          </div>
        </template>
        <template v-slot:[`item.contact_no`]="{ item }">
          <span v-if="item.country_code && item.contact_no">
            {{ item.country_code }} {{ item.contact_no }}
          </span>
          <span v-else>-</span>
        </template>
        <template v-slot:[`item.organization_address`]="{ item }">
          <v-tooltip
            v-if="
              item.organization_address && item.organization_address.length > 30
            "
            location="top"
          >
            <template v-slot:activator="{ props }">
              <span v-bind="props"
                >{{ item.organization_address.substring(0, 30) }}...</span
              >
            </template>
            <span
              style="
                max-width: 400px;
                display: inline-block;
                word-wrap: break-word;
              "
              >{{ item.organization_address }}</span
            >
          </v-tooltip>
          <span v-else>{{ item.organization_address || "-" }}</span>
        </template>
        <template v-slot:[`item.licence_info`]="{ item }">
          <span>{{ item.licence_info ? item.licence_info : "-" }}</span>
        </template>
        <template v-slot:[`item.license_type`]="{ item }">
          <span>{{
            item.license_type
              ? item.license_type == "HYBRID"
                ? "BOTH"
                : item.license_type
              : "-"
          }}</span>
        </template>
        <template v-slot:[`item.mrr_license_count`]="{ item }">
          <span>{{
            item.mrr_license_count ? item.mrr_license_count : "-"
          }}</span>
        </template>

        <template v-slot:[`item.annual_plan_price`]="{ item }">
          <span>{{
            item.annual_plan_price ? item.annual_plan_price : "-"
          }}</span>
        </template>

        <template v-slot:[`item.licensed_user_count`]="{ item }">
          <span>{{
            item.licensed_user_count ? item.licensed_user_count : "-"
          }}</span>
        </template>
        <template v-slot:[`item.subscription_start_date`]="{ item }">
          <span>{{
            item.subscription_start_date
              ? formatTimestamp(item.subscription_start_date)
              : "-"
          }}</span>
        </template>
        <template v-slot:[`item.subscription_end_date`]="{ item }">
          <span>{{
            item.subscription_end_date
              ? formatTimestamp(item.subscription_end_date)
              : "-"
          }}</span>
        </template>
        <template v-slot:[`item.renewal_status`]="{ item }">
          <v-tooltip
            v-if="item?.renewal_settings?.is_under_renewal"
            location="top"
          >
            <template #activator="{ props }">
              <v-icon v-bind="props" size="16" color="warning">
                mdi-information-outline
              </v-icon>
            </template>

            <div style="max-width: 250px">
              <div>
                <b>Users:</b> {{ item.renewal_settings.renewal_user_count }}
              </div>
              <div>
                <b>Price:</b> ${{ item.renewal_settings.renewal_price }}
              </div>
            </div>
          </v-tooltip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn icon size="small" variant="text" v-bind="props">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item title="Edit" @click="editOrganization(item)">
                <template v-slot:prepend>
                  <v-icon size="small" color="primary">mdi-pencil</v-icon>
                </template>
              </v-list-item>
              <v-list-item
                title="Renewal"
                @click="renewOrganization(item)"
                v-if="
                  item.license_type != 'TRIAL' &&
                  !item?.renewal_settings?.is_under_renewal
                "
              >
                <template v-slot:prepend>
                  <v-icon size="small" color="primary">mdi-autorenew</v-icon>
                </template>
              </v-list-item>
              <v-list-item title="Users" @click="openUsersDialog(item)">
                <template #prepend>
                  <v-icon size="small" color="primary"
                    >mdi-account-multiple</v-icon
                  >
                </template>
              </v-list-item>
              <v-list-item
                v-if="item.exposed_api_access_enabled === true"
                title="Organization API Usage"
                @click="openApiUsageDialog(item)"
              >
                <template #prepend>
                  <v-icon size="small" color="primary"
                    >mdi-chart-box-outline</v-icon
                  >
                </template>
              </v-list-item>
              <v-list-item
                :title="
                  item.exposed_api_access_enabled
                    ? 'Disable Exposed API Access'
                    : 'Enable Exposed API Access'
                "
                :disabled="exposedApiTogglingId === item.organization_id"
                @click="toggleExposedApiAccess(item)"
              >
                <template #prepend>
                  <v-icon
                    size="small"
                    :color="
                      item.exposed_api_access_enabled ? 'error' : 'success'
                    "
                  >
                    {{
                      item.exposed_api_access_enabled
                        ? "mdi-api-off"
                        : "mdi-api"
                    }}
                  </v-icon>
                </template>
              </v-list-item>
              <v-list-item title="Deactive" @click="deleteOrganization(item)">
                <template v-slot:prepend>
                  <v-icon size="small" color="error">mdi-delete</v-icon>
                </template>
              </v-list-item>
              <v-list-item title="Audit Log" @click="openAuditLog(item)">
                <template #prepend>
                  <v-icon size="small" color="primary">mdi-history</v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <CreateOrganizationDialog
      v-model="showCreateDialog"
      @created="handleOrganizationCreated"
    />

    <EditOrganizationDialog
      v-model="showEditDialog"
      :organization="selectedOrganization"
      @updated="handleOrganizationUpdated"
    />
    <OrganizationUsersDialog
      v-model="showUsersDialog"
      :organization="selectedOrganization"
      @refresh-organizations="refreshOrganizations"
    />
    <DeleteOrganizationDialog
      v-model="showDeleteDialog"
      :organization="selectedOrganization"
      @deleted="handleOrganizationDeleted"
    />
    <RenewPlanDialog
      v-model="showRenewDialog"
      :organization="selectedOrganization"
      @saved="handleOrganizationDeleted"
    />
    <OrganizationHeaders
      v-model="showSettingsDialog"
      :headers="headers"
      @update:headers="applyHeaders"
      :defaultHeaders="defaultHeaders"
    />
    <AuditLogDialog
      v-model="showAuditDialog"
      :organizationId="selectedOrganization?.organization_id"
    />
  </div>
</template>

<script>
import AuditLogDialog from "@/components/Organization/Dailogs/AuditLogDialog.vue";
import OrganizationHeaders from "@/components/Organization/Dailogs/OrganizationHeaders.vue";
import OrganizationUsersDialog from "@/components/Organization/Dailogs/OrganizationUsersDialog.vue";
import OrganizationApiUsageDialog from "@/components/Organization/Dailogs/OrganizationApiUsageDialog.vue";
import CreateOrganizationDialog from "../../components/Organization/Dailogs/CreateOrganizationDialog.vue";
import EditOrganizationDialog from "../../components/Organization/Dailogs/EditOrganizationDialog.vue";
import DeleteOrganizationDialog from "../../components/Organization/Dailogs/DeleteOrganizationDialog.vue";
import RenewPlanDialog from "../../components/Organization/Dailogs/RenewPlanDialog.vue";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";
import GetOrganizations from "@/mixins/Organization/GetOrganizations";
import GetLicense from "@/mixins/Licensesetting/GetLicense";
import { generateClient } from "aws-amplify/api";
import { edit_organization } from "@/graphql/mutations";

const client = generateClient();

const DEFAULT_EXPOSED_API_CONFIG = [
  { api_name: "list_facilities", limit: 5, cost_per_call: 0.02 },
  { api_name: "list_facility_residents", limit: 10, cost_per_call: 0.02 },
  {
    api_name: "list_resident_recommendations",
    limit: 13,
    cost_per_call: 0.02,
  },
  { api_name: "list_resident_medications", limit: 7, cost_per_call: 0.02 },
  { api_name: "list_reports", limit: 9, cost_per_call: 0.02 },
  { api_name: "list_all_recommendations", limit: 10, cost_per_call: 0.02 },
  { api_name: "list_facility_medications", limit: 10, cost_per_call: 0.02 },
];

function parseExposedApiConfig(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === "object") {
      return Object.keys(parsed).map((api_name) => ({
        api_name,
        limit: Number(parsed[api_name]?.limit) || 0,
        cost_per_call: Number(parsed[api_name]?.cost_per_call) || 0,
      }));
    }
    return [];
  } catch (error) {
    console.error("Error parsing exposed_api_config:", error);
    return [];
  }
}

function buildExposedApiConfig(existing = []) {
  const existingList = Array.isArray(existing)
    ? existing
    : parseExposedApiConfig(existing);
  return DEFAULT_EXPOSED_API_CONFIG.map((defaults) => {
    const match = existingList.find(
      (item) => item?.api_name === defaults.api_name
    );
    return {
      api_name: defaults.api_name,
      limit:
        match?.limit !== undefined && match?.limit !== null
          ? Number(match.limit)
          : defaults.limit,
      cost_per_call:
        match?.cost_per_call !== undefined && match?.cost_per_call !== null
          ? Number(match.cost_per_call)
          : defaults.cost_per_call,
    };
  });
}

export default {
  name: "OrganizationView",
  mixins: [GetOrganizations, GetLicense],
  components: {
    CreateOrganizationDialog,
    EditOrganizationDialog,
    DeleteOrganizationDialog,
    RenewPlanDialog,
    OrganizationUsersDialog,
    OrganizationApiUsageDialog,
    OrganizationHeaders,
    AuditLogDialog,
    SnackbarComponent,
  },
  data() {
    return {
      showCreateDialog: false,
      showAuditDialog: false,
      showEditDialog: false,
      showSettingsDialog: false,
      showDeleteDialog: false,
      showUsersDialog: false,
      showRenewDialog: false,
      showApiUsageDialog: false,
      tableLoading: false,
      exposedApiTogglingId: null,
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      selectedOrganization: null,
      selectedIndex: -1,
      organizations: [],
      search: "",
      windowHeight: window.innerHeight,
      headers: [],
      defaultHeaders: [
        {
          title: "Organization Name",
          key: "organization_name",
          sortable: true,
        },
        {
          title: "Registered Address",
          key: "organization_address",
          sortable: true,
        },
        { title: "Compliance License", key: "licence_info", sortable: true },
        { title: "Contact Name", key: "owner_name", sortable: true },

        { title: "Email", key: "owner_email_id", sortable: true },
        { title: "Mobile Number", key: "contact_no", sortable: true },
        { title: "License Type", key: "license_type", sortable: true },
        { title: "Annual Price", key: "annual_plan_price", sortable: true },

        {
          title: "Anuual License UserCount",
          key: "licensed_user_count",
          sortable: true,
        },
        {
          title: "MRR License Count",
          key: "mrr_license_count",
          sortable: true,
        },

        {
          title: "Subscription Start Date",
          key: "subscription_start_date",
          sortable: true,
        },
        {
          title: "Subscription End Date",
          key: "subscription_end_date",
          sortable: true,
        },
        {
          title: "Renewal Status",
          key: "renewal_status",
          sortable: false,
        },
        { title: "Actions", key: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    visibleHeaders() {
      return (this.headers || []).filter(
        (h) => h.default === undefined || h.default === true
      );
    },
    noDataText() {
      return "No organizations found. Click 'Create Organization' to add one.";
    },
    tableHeight() {
      return this.windowHeight - 200;
    },
    filteredOrganizations() {
      if (!this.search) {
        return this.organizations;
      }

      const searchTerm = this.search.toLowerCase().trim();
      return this.organizations.filter((org) => {
        const contact = [org.country_code, org.contact_no]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return (
          (org.organization_name &&
            org.organization_name.toLowerCase().includes(searchTerm)) ||
          (org.organization_address &&
            org.organization_address.toLowerCase().includes(searchTerm)) ||
          (org.licence_info &&
            String(org.licence_info).toLowerCase().includes(searchTerm)) ||
          (org.owner_name &&
            org.owner_name.toLowerCase().includes(searchTerm)) ||
          (org.owner_email_id &&
            org.owner_email_id.toLowerCase().includes(searchTerm)) ||
          (org.license_type &&
            String(org.license_type).toLowerCase().includes(searchTerm)) ||
          contact.includes(searchTerm)
        );
      });
    },
  },
  async mounted() {
    this.updateWindowHeight();
    this.applyHeaders();
    window.addEventListener("resize", this.updateWindowHeight);
    this.tableLoading = true;
    try {
      await this.fetchOrganizations();
      this.organizations = this.organizationsList;
    } catch (error) {
      console.error("Error loading organizations:", error);
    } finally {
      this.tableLoading = false;
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateWindowHeight);
  },
  methods: {
    openApiUsageDialog(org) {
      this.selectedOrganization = { ...org };
      this.showApiUsageDialog = true;
    },
    async toggleExposedApiAccess(org) {
      if (!org?.organization_id || this.exposedApiTogglingId) return;

      const nextEnabled = org.exposed_api_access_enabled !== true;
      let configSource = parseExposedApiConfig(org.exposed_api_config);
      if (nextEnabled && !configSource.length) {
        configSource = parseExposedApiConfig(
          this.LicenseList?.default_api_endpoint_settings
        );
      }
      const exposed_api_config = nextEnabled
        ? JSON.stringify(buildExposedApiConfig(configSource))
        : JSON.stringify([]);
      const show_api_call_cost = nextEnabled
        ? this.LicenseList?.default_show_api_call_cost === true ||
          String(this.LicenseList?.default_show_api_call_cost)
            .trim()
            .toLowerCase() === "true"
        : false;

      this.exposedApiTogglingId = org.organization_id;
      try {
        const response = await client.graphql({
          query: edit_organization,
          variables: {
            input: {
              organization_id: org.organization_id,
              exposed_api_access_enabled: nextEnabled,
              exposed_api_config,
              show_api_call_cost,
            },
          },
        });
        const result = JSON.parse(response.data.edit_organization);
        if (result.status !== "SUCCESS") {
          throw new Error(
            result.status_message || "Failed to update Exposed API Access"
          );
        }

        org.exposed_api_access_enabled = nextEnabled;
        org.exposed_api_config = exposed_api_config;
        org.show_api_call_cost = show_api_call_cost;

        const listItem = this.organizations.find(
          (o) => o.organization_id === org.organization_id
        );
        if (listItem) {
          listItem.exposed_api_access_enabled = nextEnabled;
          listItem.exposed_api_config = exposed_api_config;
          listItem.show_api_call_cost = show_api_call_cost;
        }

        this.snackbar = {
          show: true,
          message:
            result.status_message ||
            (nextEnabled
              ? "Exposed API Access enabled"
              : "Exposed API Access disabled"),
          color: "success",
        };
      } catch (error) {
        console.error("Error toggling Exposed API Access:", error);
        this.snackbar = {
          show: true,
          message: error.message || "Failed to update Exposed API Access",
          color: "error",
        };
      } finally {
        this.exposedApiTogglingId = null;
      }
    },
    openAuditLog(org) {
      this.selectedOrganization = org;
      this.showAuditDialog = true;
    },
    async refreshOrganizations() {
      this.tableLoading = true;
      try {
        await this.fetchOrganizations();
        this.organizations = this.organizationsList;
      } catch (error) {
        console.error("Error refreshing organizations:", error);
      } finally {
        this.tableLoading = false;
      }
    },
    async applyHeaders() {
      await this.fetchLicense();
      this.headers =
        this.LicenseList?.bms_tab_configuration == undefined
          ? this.defaultHeaders
          : this.LicenseList.bms_tab_configuration;
    },
    openSettingsDialog() {
      this.showSettingsDialog = true;
    },
    openUsersDialog(org) {
      this.selectedOrganization = { ...org };
      this.showUsersDialog = true;
    },
    formatTimestamp(timestamp) {
      const numeric = Number(timestamp);
      if (isNaN(numeric)) return "";
      const ms = numeric < 1e12 ? numeric * 1000 : numeric;
      const date = new Date(ms);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    updateWindowHeight() {
      this.windowHeight = window.innerHeight;
    },
    openCreateDialog() {
      this.showCreateDialog = true;
    },
    async handleOrganizationCreated() {
      this.tableLoading = true;
      try {
        await this.fetchOrganizations();
        this.organizations = this.organizationsList;
      } catch (error) {
        console.error("Error loading organizations:", error);
      } finally {
        this.tableLoading = false;
      }
    },
    editOrganization(org) {
      this.selectedOrganization = { ...org };
      this.selectedIndex = this.organizations.findIndex(
        (o) =>
          o.organizationName === org.organizationName && o.email === org.email
      );
      this.showEditDialog = true;
    },
    async handleOrganizationUpdated() {
      this.tableLoading = true;
      try {
        await this.fetchOrganizations();
        this.organizations = this.organizationsList;
      } catch (error) {
        console.error("Error loading organizations:", error);
      } finally {
        this.tableLoading = false;
      }
      this.selectedIndex = -1;
      this.selectedOrganization = null;
    },
    deleteOrganization(org) {
      this.selectedOrganization = { ...org };
      this.selectedIndex = this.organizations.findIndex(
        (o) =>
          o.organizationName === org.organizationName && o.email === org.email
      );
      this.showDeleteDialog = true;
    },
    renewOrganization(org) {
      this.selectedOrganization = { ...org };
      this.selectedIndex = this.organizations.findIndex(
        (o) =>
          o.organizationName === org.organizationName && o.email === org.email
      );
      this.showRenewDialog = true;
    },
    async handleOrganizationDeleted() {
      this.tableLoading = true;
      try {
        await this.fetchOrganizations();
        this.organizations = this.organizationsList;
      } catch (error) {
        console.error("Error loading organizations:", error);
      } finally {
        this.tableLoading = false;
      }
      this.selectedIndex = -1;
      this.selectedOrganization = null;
    },
  },
};
</script>
